import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import type { MRT_TableOptions } from 'material-react-table'
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import React, { useEffect, useLayoutEffect, useState } from 'react'
import type { MrtRowData } from './mrtCollectionTypes'
import { getBodyLayoutDeps, MrtBodyLayoutContext, MrtBodyRows } from './MrtBodyRows'
import { createMrtRowSelectionStore, MrtRowSelectionStoreContext } from './mrtRowSelectionContext'

type MrtTableProps<T extends MrtRowData> = {
  options: MRT_TableOptions<T>
  // Something that fullscreen would cover is showing, such as a drawer or a view in place of the rows.
  exitFullScreen: boolean
}

const MrtTable = <T extends MrtRowData>({ options, exitFullScreen }: MrtTableProps<T>): React.ReactElement => {
  const [rowSelectionStore] = useState(createMrtRowSelectionStore)
  const { onRowSelectionChange } = options
  const table = useMaterialReactTable<T>({
    ...options,
    onRowSelectionChange:
      onRowSelectionChange &&
      (updater => {
        const next = typeof updater === 'function' ? updater(rowSelectionStore.getSelection()) : updater
        rowSelectionStore.setSelection(next)
        onRowSelectionChange(next)
      }),
    memoMode: 'table-body',
    muiTableBodyProps: ({ table: bodyTable }) => ({ children: <MrtBodyRows table={bodyTable} /> }),
  })

  const { rowSelection } = table.getState()
  useLayoutEffect(() => rowSelectionStore.setSelection(rowSelection), [rowSelection, rowSelectionStore])

  // MRT's fullscreen paper is position:fixed at the modal zIndex, which covers the side drawers (they
  // sit at the lower drawer zIndex) and anything shown beside the table. Exit fullscreen so they stay visible.
  useEffect(() => {
    if (exitFullScreen && table.getState().isFullScreen) {
      table.setIsFullScreen(false)
    }
  }, [exitFullScreen, table])

  return (
    <MrtBodyLayoutContext.Provider value={getBodyLayoutDeps(table.getState(), options.columns, table.options.data)}>
      <MrtRowSelectionStoreContext.Provider value={rowSelectionStore}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <MaterialReactTable table={table} />
        </LocalizationProvider>
      </MrtRowSelectionStoreContext.Provider>
    </MrtBodyLayoutContext.Provider>
  )
}

export default MrtTable
